import type {
  BillingActivationFailure,
  BillingInterval,
  PlanDefinition,
  PlanFeatures,
  PlanId
} from '@tandemone/types'
import { ACTIVATION_RETRY_LABELS, PLAN_CATALOG } from '@tandemone/types'
import { Check, Minus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'

import {
  formatPlanPrice,
  formatSeats,
  intervalLabel,
  yearlySavingMonths
} from './plan-price'

/* labels live here, values come from PLAN_CATALOG, so marketing copy and
 * enforcement cannot drift. */
const FEATURE_ROWS: { key: keyof PlanFeatures; label: string }[] = [
  { key: 'isListedInDirectory', label: 'Verified profile & directory listing' },
  { key: 'canReceiveRfi', label: 'Receive RFIs' },
  /* both rows read the same flag: seeing the buyer and replying to them are one
   * paywall, since a reply names a buyer a locked recipient cannot see. */
  { key: 'canViewRfiDetail', label: 'Buyer details & volumes on RFIs' },
  { key: 'canViewRfiDetail', label: 'Respond to RFIs' },
  { key: 'canSendRfi', label: 'Send RFIs' },
  /* gates /insights/engagement only, not the whole insights page. */
  { key: 'canViewDocumentInsights', label: 'Buyer engagement insights' }
]

const REP_COVERAGE_LABEL = 'Covers all your suppliers'

type Action = {
  label: string
  /* warning is the retry after a declined card: it is the same checkout the
   * upgrade runs, but it has to read as the fix for the banner above it. */
  variant: 'default' | 'outline' | 'warning'
  kind:
    | 'upgrade'
    | 'downgrade'
    | 'switch-interval'
    | 'cancel'
    | 'resume'
    | 'choose'
    | 'pay-invoice'
}

/* the two differ exactly when someone is looking at the other billing period
 * for their own tier, which is the state that offers a switch, not a badge. */
export function planCardState(input: {
  planId: PlanId
  currentPlanId: PlanId | null
  currentInterval: BillingInterval | null
  selectedInterval: BillingInterval
}): { isCurrentTier: boolean; isCurrentExactly: boolean } {
  const isCurrentTier = input.planId === input.currentPlanId
  /* a plan with no cadence of its own (free, a comped grant) matches whatever
   * the toggle says. */
  const isCurrentExactly =
    isCurrentTier &&
    (input.currentInterval === null ||
      input.currentInterval === input.selectedInterval)
  return { isCurrentTier, isCurrentExactly }
}

/* choosing differs from managing: at onboarding there is no plan to move up or
 * down from, so every tier reads as an equal pick. cadence still counts, so the
 * tier you picked can still offer the other billing period. */
export function actionFor(input: {
  planId: PlanId
  currentPlanId: PlanId | null
  currentInterval: BillingInterval | null
  selectedInterval: BillingInterval
  cancelAtPeriodEnd: boolean
  mode?: 'manage' | 'choose'
  /* the plan whose activation failed, and why. it is not the current plan:
   * they fell back to free, so without this the column offers a plain upgrade
   * and nothing on the page is the obvious fix. */
  activationFailedPlanId?: PlanId | null
  activationFailure?: BillingActivationFailure | null
  /* the plan they picked and were invoiced for, still unpaid. */
  unpaidPlanId?: PlanId | null
}): Action | null {
  const { planId, currentPlanId, selectedInterval } = input
  const { isCurrentTier, isCurrentExactly } = planCardState(input)
  const isFree = PLAN_CATALOG[planId].monthlyPriceCents === 0

  /* ahead of every other branch: this column is the recovery, whatever the
   * tier arithmetic would otherwise call it. */
  if (
    input.activationFailure &&
    input.activationFailedPlanId === planId &&
    !isCurrentTier
  ) {
    return {
      label: ACTIVATION_RETRY_LABELS[input.activationFailure],
      variant: 'warning',
      kind: 'upgrade'
    }
  }

  /* they already chose this plan and hold its bill, so the column settles it
   * rather than reading as a fresh upgrade. entitlements resolve to free while
   * it is unpaid, which is what would otherwise call this an upgrade. */
  if (input.unpaidPlanId === planId) {
    return { label: 'Pay now', variant: 'default', kind: 'pay-invoice' }
  }

  if (input.mode === 'choose') {
    if (isCurrentExactly) return null
    if (isCurrentTier) {
      return {
        label: `Switch to ${intervalLabel(selectedInterval).toLowerCase()} billing`,
        variant: 'default',
        kind: 'switch-interval'
      }
    }
    return {
      label: `Choose ${PLAN_CATALOG[planId].label}`,
      variant: isFree ? 'outline' : 'default',
      kind: 'choose'
    }
  }

  if (!currentPlanId) return null

  if (isCurrentTier) {
    if (isFree) return null
    if (input.cancelAtPeriodEnd) {
      return {
        label: 'Resume subscription',
        variant: 'default',
        kind: 'resume'
      }
    }
    /* not gated on a non-null currentInterval: a stripe row whose price we no
     * longer recognise stores null, and the switch must stay reachable. */
    if (input.currentInterval !== selectedInterval) {
      return {
        label: `Switch to ${intervalLabel(selectedInterval).toLowerCase()} billing`,
        variant: 'default',
        kind: 'switch-interval'
      }
    }
    return {
      label: 'Cancel subscription',
      variant: 'outline',
      kind: 'cancel'
    }
  }

  const isUpgrade =
    PLAN_CATALOG[planId].monthlyPriceCents >
    PLAN_CATALOG[currentPlanId].monthlyPriceCents

  return isUpgrade
    ? {
        label: `Upgrade to ${PLAN_CATALOG[planId].label}`,
        variant: 'default',
        kind: 'upgrade'
      }
    : {
        label: `Downgrade to ${PLAN_CATALOG[planId].label}`,
        variant: 'outline',
        kind: 'downgrade'
      }
}

type PlanColumn = {
  planId: PlanId
  plan: PlanDefinition
  isCurrentTier: boolean
  isCurrentExactly: boolean
  action: Action | null
}

type LayoutProps = {
  columns: PlanColumn[]
  interval: BillingInterval
  mode: 'manage' | 'choose'
  repCoverage: boolean
  highlightPlanId?: PlanId | null
  disabled?: boolean
  onAction?: (planId: PlanId, action: Action) => void
}

function IntervalToggle({
  interval,
  savingMonths,
  onChange
}: {
  interval: BillingInterval
  savingMonths: number
  onChange: (interval: BillingInterval) => void
}) {
  return (
    <ToggleGroup
      type='single'
      variant='outline'
      value={interval}
      onValueChange={value => {
        if (value) onChange(value as BillingInterval)
      }}
      className='w-full rounded-lg border border-border p-0.5 sm:w-fit'
    >
      <ToggleGroupItem
        value='month'
        className='flex-1 px-4 data-[state=on]:bg-accent data-[state=on]:text-white sm:flex-none'
      >
        Monthly
      </ToggleGroupItem>
      <ToggleGroupItem
        value='year'
        className='flex-1 gap-2 px-4 data-[state=on]:bg-accent data-[state=on]:text-white sm:flex-none'
      >
        Yearly
        {savingMonths > 0 && (
          <span className='rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800'>
            {savingMonths} mo free
          </span>
        )}
      </ToggleGroupItem>
    </ToggleGroup>
  )
}

function Mark({ on }: { on: boolean }) {
  return on ? (
    <Check className='size-4 shrink-0 text-emerald-700' />
  ) : (
    <Minus className='size-4 shrink-0 text-ink-muted' />
  )
}

function ActionButton({
  column,
  disabled,
  onAction
}: {
  column: PlanColumn
  disabled?: boolean
  onAction?: (planId: PlanId, action: Action) => void
}) {
  const action = column.action
  if (!onAction || !action) return null
  const warning = action.variant === 'warning'
  return (
    <Button
      type='button'
      variant={action.variant === 'warning' ? 'default' : action.variant}
      disabled={disabled}
      className={cn(
        'w-full',
        warning &&
          'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400'
      )}
      onClick={() => onAction(column.planId, action)}
    >
      {action.label}
    </Button>
  )
}

function CurrentLabel({
  column,
  mode
}: {
  column: PlanColumn
  mode: 'manage' | 'choose'
}) {
  if (!column.isCurrentExactly) return null
  return (
    <Badge className='border-transparent bg-accent-light text-accent'>
      {mode === 'choose' ? 'Selected' : 'Current'}
    </Badge>
  )
}

function ComparisonTable({
  columns,
  interval,
  mode,
  repCoverage,
  highlightPlanId,
  disabled,
  onAction
}: LayoutProps) {
  const columnClass = (column: PlanColumn) =>
    cn(
      'px-4 text-center align-middle',
      column.isCurrentTier && 'bg-accent-light/50',
      /* amber-50 alone is a light wash that lands as a pale grey slab over the
       * dark ground, which reads as a rendering fault rather than emphasis. */
      highlightPlanId === column.planId &&
        !column.isCurrentTier &&
        'bg-amber-50/70 dark:bg-amber-950/30'
    )

  /* fixed layout so the plan columns stay equal instead of being sized by the
   * longest label, which is what wraps a price onto three lines. */
  return (
    <table className='w-full table-fixed border-collapse text-[13px]'>
      <thead>
        <tr>
          <th className='w-[34%] px-1 pb-4 text-left font-normal' />
          {columns.map(column => (
            <th
              key={column.planId}
              scope='col'
              className={cn(columnClass(column), 'pb-4 pt-4 align-top')}
            >
              <span className='block text-[13px] font-medium text-ink'>
                {column.plan.label}
              </span>
              <span className='mt-1 block whitespace-nowrap font-display text-[20px] leading-none tabular-nums text-ink'>
                {formatPlanPrice(column.plan, interval)}
              </span>
              {column.isCurrentExactly && (
                <span className='mt-1.5 block text-[12px] font-medium text-accent'>
                  {mode === 'choose' ? 'Selected' : 'Current'}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {FEATURE_ROWS.map(row => (
          <tr key={row.label} className='border-t border-border'>
            <th
              scope='row'
              className='px-1 py-3 text-left font-normal text-ink'
            >
              {row.label}
            </th>
            {columns.map(column => (
              <td
                key={column.planId}
                className={cn(columnClass(column), 'py-3')}
              >
                <span className='flex justify-center'>
                  <Mark on={column.plan.features[row.key]} />
                </span>
              </td>
            ))}
          </tr>
        ))}
        {repCoverage && (
          <tr className='border-t border-border'>
            <th
              scope='row'
              className='px-1 py-3 text-left font-normal text-ink'
            >
              {REP_COVERAGE_LABEL}
            </th>
            {columns.map(column => (
              <td
                key={column.planId}
                className={cn(columnClass(column), 'py-3')}
              >
                <span className='flex justify-center'>
                  <Mark on={column.plan.coversRepSuppliers} />
                </span>
              </td>
            ))}
          </tr>
        )}
        <tr className='border-t border-border'>
          <th scope='row' className='px-1 py-3 text-left font-normal text-ink'>
            Seats
          </th>
          {columns.map(column => (
            <td
              key={column.planId}
              className={cn(
                columnClass(column),
                'py-3 tabular-nums text-ink-soft'
              )}
            >
              {formatSeats(column.plan.seatLimit)}
            </td>
          ))}
        </tr>
      </tbody>

      {onAction && (
        <tfoot>
          <tr className='border-t border-border'>
            <td className='px-1' />
            {columns.map(column => (
              <td
                key={column.planId}
                className={cn(columnClass(column), 'py-4')}
              >
                <ActionButton
                  column={column}
                  disabled={disabled}
                  onAction={onAction}
                />
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  )
}

/* one card per plan, each listing every row rather than only what the tier
 * adds: stacked, the cards are the only way left to compare them. */
function PlanCards({
  columns,
  interval,
  mode,
  repCoverage,
  highlightPlanId,
  disabled,
  onAction
}: LayoutProps) {
  return (
    <div className='flex flex-col gap-4'>
      {columns.map(column => (
        <div
          key={column.planId}
          className={cn(
            'flex flex-col gap-4 rounded-xl border border-border p-4',
            column.isCurrentTier && 'border-accent ring-2 ring-accent/25',
            highlightPlanId === column.planId &&
              !column.isCurrentTier &&
              'border-accent ring-2 ring-accent/30'
          )}
        >
          <div className='flex items-start justify-between gap-3'>
            <div>
              <p className='text-[13px] font-medium text-ink'>
                {column.plan.label}
              </p>
              <p className='mt-1 font-display text-[22px] leading-none tabular-nums text-ink'>
                {formatPlanPrice(column.plan, interval)}
              </p>
            </div>
            <CurrentLabel column={column} mode={mode} />
          </div>

          <ul className='flex flex-col gap-2'>
            {FEATURE_ROWS.map(row => {
              const on = column.plan.features[row.key]
              return (
                <li
                  key={row.label}
                  className={cn(
                    'flex items-start gap-2 text-[13px]',
                    on ? 'text-ink' : 'text-ink-muted'
                  )}
                >
                  <Mark on={on} />
                  <span>{row.label}</span>
                </li>
              )
            })}
            {repCoverage && (
              <li
                className={cn(
                  'flex items-start gap-2 text-[13px]',
                  column.plan.coversRepSuppliers ? 'text-ink' : 'text-ink-muted'
                )}
              >
                <Mark on={column.plan.coversRepSuppliers} />
                <span>{REP_COVERAGE_LABEL}</span>
              </li>
            )}
            <li className='flex items-start gap-2 text-[13px] text-ink'>
              <Mark on />
              <span>{formatSeats(column.plan.seatLimit)} seats</span>
            </li>
          </ul>

          <ActionButton
            column={column}
            disabled={disabled}
            onAction={onAction}
          />
        </div>
      ))}
    </div>
  )
}

export function PlanComparison({
  planIds,
  interval,
  currentPlanId,
  currentInterval,
  cancelAtPeriodEnd,
  highlightPlanId,
  disabled,
  mode = 'manage',
  activationFailedPlanId = null,
  activationFailure = null,
  unpaidPlanId = null,
  repCoverage = false,
  onIntervalChange,
  onAction
}: {
  planIds: PlanId[]
  interval: BillingInterval
  /* null only while choosing, before anything is picked. */
  currentPlanId: PlanId | null
  currentInterval: BillingInterval | null
  cancelAtPeriodEnd: boolean
  highlightPlanId?: PlanId | null
  disabled?: boolean
  mode?: 'manage' | 'choose'
  activationFailedPlanId?: PlanId | null
  activationFailure?: BillingActivationFailure | null
  unpaidPlanId?: PlanId | null
  /* a manufacturing rep's plan covers its suppliers too. */
  repCoverage?: boolean
  onIntervalChange?: (interval: BillingInterval) => void
  onAction?: (planId: PlanId, action: Action) => void
}) {
  const columns: PlanColumn[] = planIds.map(planId => {
    const plan = PLAN_CATALOG[planId]
    /* the tint follows the tier, the label follows tier and cadence: a monthly
     * subscriber looking at yearly prices is still on this plan, but the price
     * on screen is not the one they hold. */
    const { isCurrentTier, isCurrentExactly } = planCardState({
      planId,
      currentPlanId,
      currentInterval,
      selectedInterval: interval
    })
    return {
      planId,
      plan,
      isCurrentTier,
      isCurrentExactly,
      action: actionFor({
        planId,
        currentPlanId,
        currentInterval,
        selectedInterval: interval,
        cancelAtPeriodEnd,
        mode,
        activationFailedPlanId,
        activationFailure,
        unpaidPlanId
      })
    }
  })

  const savingMonths = Math.max(
    0,
    ...planIds.map(id => yearlySavingMonths(PLAN_CATALOG[id]))
  )

  const layout: LayoutProps = {
    columns,
    interval,
    mode,
    repCoverage,
    highlightPlanId,
    disabled,
    onAction
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'>
        <h2 className='text-sm font-medium text-ink'>Compare plans</h2>
        {onIntervalChange && (
          <IntervalToggle
            interval={interval}
            savingMonths={savingMonths}
            onChange={onIntervalChange}
          />
        )}
      </div>

      {/* three priced columns next to a label column need real width before
          they stop wrapping, so the cards carry everything below lg. */}
      <div className='hidden lg:block'>
        <ComparisonTable {...layout} />
      </div>
      <div className='lg:hidden'>
        <PlanCards {...layout} />
      </div>
    </div>
  )
}
